import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Trophy, RotateCcw } from "lucide-react";
import { QuestionImage } from "@/components/app/EvalLive";

const SECS = 20;
const NOTES = ["bg-rose-100 border-rose-300", "bg-sky-100 border-sky-300", "bg-amber-100 border-amber-300", "bg-emerald-100 border-emerald-300", "bg-violet-100 border-violet-300", "bg-pink-100 border-pink-300"];
const TILT = [-1.5, 1, -0.5, 1.5, -1, 0.5];
const LETTERS = "ABCDEFGH";
const paper = {
  backgroundColor: "#fffdf6",
  backgroundImage: "linear-gradient(90deg, transparent 46px, rgba(239,68,68,.45) 46px, rgba(239,68,68,.45) 48px, transparent 48px), repeating-linear-gradient(0deg, transparent 0 31px, rgba(59,130,246,.18) 31px 32px)",
};
const hand = { fontFamily: "'Patrick Hand', 'Comic Sans MS', cursive" };
const paperR = { backgroundColor: "#fffdf6", backgroundImage: "repeating-linear-gradient(0deg, transparent 0 31px, rgba(59,130,246,.18) 31px 32px)" };

function useHandFont() {
  useEffect(() => {
    if (document.getElementById("font-patrick")) return;
    const l = document.createElement("link");
    l.id = "font-patrick"; l.rel = "stylesheet"; l.href = "https://fonts.googleapis.com/css2?family=Patrick+Hand&display=swap";
    document.head.appendChild(l);
  }, []);
}

function Mark({ ok }) {
  return (
    <motion.div initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 260, damping: 14 }}
      className={`w-24 h-24 rounded-full flex items-center justify-center shadow-xl ${ok ? "bg-emerald-500" : "bg-rose-500"}`} data-testid={ok ? "nb-mark-ok" : "nb-mark-bad"}>
      <svg viewBox="0 0 52 52" className="w-14 h-14" fill="none" stroke="white" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round">
        {ok ? <motion.path d="M14 27 l8 8 l16 -18" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.45, delay: 0.15 }} />
          : <><motion.path d="M16 16 L36 36" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.3, delay: 0.15 }} />
            <motion.path d="M36 16 L16 36" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.3, delay: 0.4 }} /></>}
      </svg>
    </motion.div>
  );
}

function Burst() {
  return Array.from({ length: 14 }).map((_, k) => (
    <motion.span key={k} className={`absolute w-2.5 h-2.5 rounded-full ${["bg-amber-400", "bg-emerald-400", "bg-sky-400", "bg-rose-400"][k % 4]}`}
      initial={{ x: 0, y: 0, opacity: 1 }} animate={{ x: Math.cos(k / 14 * 6.28) * 110, y: Math.sin(k / 14 * 6.28) * 110, opacity: 0 }} transition={{ duration: 0.9, ease: "easeOut" }} />
  ));
}

function Ring({ left }) {
  const c = 2 * Math.PI * 20;
  return (
    <div className="relative w-14 h-14 shrink-0" data-testid="nb-timer">
      <svg viewBox="0 0 48 48" className="w-14 h-14 -rotate-90"><circle cx="24" cy="24" r="20" stroke="#e5e7eb" strokeWidth="5" fill="none" />
        <circle cx="24" cy="24" r="20" strokeWidth="5" fill="none" strokeLinecap="round" className={left <= 5 ? "stroke-rose-500" : "stroke-navy"} strokeDasharray={c} strokeDashoffset={c * (1 - left / SECS)} style={{ transition: "stroke-dashoffset 1s linear" }} /></svg>
      <span className={`absolute inset-0 flex items-center justify-center font-black text-lg ${left <= 5 ? "text-rose-600" : "text-navy"}`}>{left}</span>
    </div>
  );
}

function Options({ q, ans, pick, right, match, setMatch }) {
  const st = (v) => (!ans ? "" : String(v) === String(q.answer) ? "ring-4 ring-emerald-400" : ans.v === v ? "ring-4 ring-rose-400 opacity-80" : "opacity-40");
  const card = (key, k, label, v, n = k) => (
    <motion.button key={key} type="button" disabled={!!ans} onClick={() => pick(v)} initial={{ opacity: 0, y: 24, rotate: 0 }} animate={{ opacity: 1, y: 0, rotate: TILT[k % 6], x: ans && ans.v === v && !ans.ok ? [0, -8, 8, -5, 5, 0] : 0 }}
      transition={{ delay: 0.15 + k * 0.08, type: "spring", stiffness: 200, damping: 18 }} whileHover={!ans ? { scale: 1.04, rotate: 0 } : {}} whileTap={!ans ? { scale: 0.96 } : {}}
      className={`${NOTES[k % 6]} border-2 rounded-lg p-3 sm:p-4 text-left text-navy shadow-md flex items-start gap-2 ${st(v)}`} style={hand} data-testid={`nb-opt-${k}`}>
      <span className="font-black text-lg leading-none mt-0.5">{LETTERS[n]})</span><span className="text-lg sm:text-xl leading-snug break-words">{label}</span>
    </motion.button>
  );
  if (q.type === "mc") return <div className="grid sm:grid-cols-2 gap-3">{q.options.map((o, k) => card(k, k, o, k))}</div>;
  if (q.type === "tf") return <div className="grid grid-cols-2 gap-3">{[[true, "Verdadero"], [false, "Falso"]].map(([v, l], k) => card(l, k + 3, l, v, k))}</div>;
  return (
    <div className="space-y-2" style={hand}>
      {q.pairs.map((p, k) => (
        <motion.div key={k} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + k * 0.07 }} className="grid grid-cols-2 gap-2 items-center">
          <span className="text-lg text-navy break-words">{p[0]}</span>
          <select disabled={!!ans} className={`rounded-md border-2 px-2 py-1.5 bg-white text-navy ${ans ? (match[k] === p[1] ? "border-emerald-400" : "border-rose-400") : "border-nardo-200"}`} value={match[k] || ""} onChange={(e) => setMatch({ ...match, [k]: e.target.value })}>
            <option value="">Elige…</option>{right.map((r) => <option key={r} value={r}>{r}</option>)}</select>
        </motion.div>
      ))}
      {!ans && <button onClick={() => pick(match)} className="w-full bg-navy text-white font-bold py-2.5 rounded-lg mt-2" data-testid="nb-match-send">Revisar</button>}
    </div>
  );
}

export function NotebookPreview({ exam, onClose }) {
  useHandFont();
  const qs = exam.questions || [];
  const [i, setI] = useState(0);
  const [ans, setAns] = useState(null);
  const [left, setLeft] = useState(SECS);
  const [match, setMatch] = useState({});
  const [score, setScore] = useState({ pts: 0, ok: 0 });
  const q = qs[i];
  const right = useMemo(() => (q?.pairs || []).map((p) => p[1]).sort(() => Math.random() - 0.5), [i]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setAns(null); setMatch({}); setLeft(SECS); }, [i]);
  useEffect(() => {
    if (ans || !q) return undefined;
    if (left <= 0) { setAns({ ok: false, v: undefined, timeout: true }); return undefined; }
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [left, ans, q]);
  const pick = (v) => {
    const ok = q.type === "match" ? q.pairs.every((p, k) => v[k] === p[1]) : String(v) === String(q.answer);
    const p = ok ? Math.round(10 * (0.5 + 0.5 * left / SECS)) : 0;
    setAns({ ok, v, p }); setScore((s) => ({ pts: s.pts + p, ok: s.ok + (ok ? 1 : 0) }));
  };
  const restart = () => { setI(0); setScore({ pts: 0, ok: 0 }); };
  const finished = i >= qs.length;
  return (
    <div className="fixed inset-0 z-[95] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6" onClick={onClose} data-testid="live-preview">
      <motion.div initial={{ opacity: 0, scale: 0.9, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} className="w-full max-w-md md:max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 text-white mb-2 px-1">
          <Trophy className="w-5 h-5 text-amber-300" /><span className="font-bold flex-1 truncate">{exam.title} <span className="font-normal text-white/70 text-sm">· vista del alumno (prueba)</span></span>
          <span className="text-sm bg-white/15 rounded-full px-3 py-1 font-bold" data-testid="nb-points">{score.pts} pts</span>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/15" data-testid="live-preview-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-1.5 mb-3 px-1">{qs.map((_, k) => <motion.div key={k} layout className={`h-1.5 rounded-full flex-1 ${k < i ? "bg-amber-400" : k === i ? "bg-white" : "bg-white/25"}`} />)}</div>
        <div className="relative rounded-2xl shadow-2xl overflow-hidden max-h-[82vh] overflow-y-auto" style={{ perspective: 1400 }}>
          <AnimatePresence mode="wait">
            {finished || !q ? (
              <motion.div key="end" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="p-10 text-center space-y-4" style={{ ...paper, ...hand }} data-testid="nb-end">
                <div className="text-4xl text-navy">{qs.length ? "¡Terminaste!" : "Este examen no tiene preguntas"}</div>
                {qs.length > 0 && <div className="text-2xl text-nardo-600">{score.ok} de {qs.length} correctas · {score.pts} puntos</div>}
                <div className="flex justify-center gap-3 pt-2">
                  {qs.length > 0 && <button onClick={restart} className="inline-flex items-center gap-2 border-2 border-navy text-navy px-5 py-2.5 rounded-lg text-lg"><RotateCcw className="w-4 h-4" /> Otra vez</button>}
                  <button onClick={onClose} className="bg-navy text-white px-5 py-2.5 rounded-lg text-lg">Cerrar</button>
                </div>
              </motion.div>
            ) : (
              <motion.div key={i} initial={{ opacity: 0, rotateY: 35, x: 80 }} animate={{ opacity: 1, rotateY: 0, x: 0 }} exit={{ opacity: 0, rotateY: -35, x: -80 }} transition={{ duration: 0.45, ease: "easeOut" }}
                className="flex flex-col md:flex-row md:min-h-[460px]" style={{ transformOrigin: "left center" }}>
                <div className="relative md:w-1/2 p-5 pl-16 md:border-r-4 md:border-double md:border-nardo-300" style={paper}>
                  <div className="absolute left-3 top-0 bottom-0 flex flex-col justify-around py-6">{[0, 1, 2, 3, 4].map((k) => <span key={k} className="w-4 h-4 rounded-full bg-slate-800/80 shadow-inner" />)}</div>
                  <div className="flex items-center gap-3 mb-3"><span className="text-sm font-bold uppercase tracking-widest text-navy/70">Pregunta {i + 1} de {qs.length}</span><span className="ml-auto">{!ans && <Ring left={left} />}</span></div>
                  <motion.h3 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-2xl sm:text-3xl text-navy leading-snug break-words" style={hand} data-testid="nb-question">{q.text}</motion.h3>
                  {q.image && <motion.div initial={{ opacity: 0, scale: 0.9, rotate: -2 }} animate={{ opacity: 1, scale: 1, rotate: -1.5 }} transition={{ delay: 0.25 }} className="mt-4 inline-block bg-white p-2 pb-4 shadow-lg"><QuestionImage tok={q.image} className="border-0 rounded-none max-h-56" /></motion.div>}
                </div>
                <div className="relative md:w-1/2 p-5 sm:p-6 flex flex-col justify-center gap-4" style={paperR}>
                  <Options q={q} ans={ans} pick={pick} right={right} match={match} setMatch={setMatch} />
                  <AnimatePresence>
                    {ans && (
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-2 pt-2" data-testid="live-preview-result">
                        <div className="relative flex items-center justify-center">{ans.ok && <Burst />}<Mark ok={ans.ok} /></div>
                        <div className={`text-2xl ${ans.ok ? "text-emerald-600" : "text-rose-600"}`} style={hand}>{ans.ok ? `¡Correcto! +${ans.p}` : ans.timeout ? "¡Se acabó el tiempo!" : "¡Casi! Esa no era"}</div>
                        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setI(i + 1)} className="bg-navy text-white font-bold px-6 py-2.5 rounded-lg inline-flex items-center gap-2" data-testid="live-preview-next">
                          {i + 1 < qs.length ? "Siguiente pregunta" : "Ver resultado"}
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
